import React, { useEffect, useState } from 'react';
import Modal from './layout/Modal';
import HBox from './layout/HBox';
import VBox from './layout/VBox';
import Botao from './Botao';
import Subtitulo from './tipografia/Subtitulo';
import Icon from '@/components/Icon';
import { mdiCheck, mdiRocketLaunch, mdiWhatsapp, mdiRobot, mdiAccountGroup, mdiFrequentlyAskedQuestions, mdiMedal, mdiStarCrescent } from '@mdi/js';
import styles from './OnboardingModal.module.css';
import Grid, { GridItem } from './layout/Grid';
import Badge from './layout/Badge';
import AlertBox from './layout/AlertBox';

type OnboardingOption = 'faq_inteligente_humano' | 'central_automatizada' | 'humano_organizado';

const options: { key: OnboardingOption; title: string; description: string; reason: string; bullets: string[]; icon: string; topChoice: string }[] = [
    {
        key: 'faq_inteligente_humano',
        title: 'FAQ inteligente + atendimento humano',
        description: 'Respostas automáticas com redirecionamento para equipe.',
        topChoice: 'Para iniciar',
        reason: 'Ideal para responder dúvidas comuns e atender clientes quando necessário.',
        bullets: [
            'Reduz perguntas repetidas',
            'Não perde clientes'
        ],
        icon: mdiFrequentlyAskedQuestions,
    },
    {
        key: 'central_automatizada',
        title: 'Central de atendimento automatizada',
        description: 'Guia o cliente passo a passo até o setor correto.',
        topChoice: 'Mais automático',
        reason: 'Perfeita para filtrar e direcionar atendimentos sem intervenção humana inicial.',
        bullets: [
            'Atende 24/7',
            'Organiza o atendimento',
            'Economiza tempo da equipe'
        ],
        icon: mdiRobot,
    },
    {
        key: 'humano_organizado',
        title: 'Atendimento humano organizado',
        description: 'Atendimento direto, sem automação inicial.',
        topChoice: 'Maior controle',
        reason: 'Ótimo se prefere um atendimento humano estruturado ou se quer começar sem automação.',
        bullets: [
            'Centraliza conversas',
            'Maior controle sobre interações'
        ],
        icon: mdiAccountGroup,
    },
];

const OnboardingModal = () => {
    const [loading, setLoading] = useState(true);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [step, setStep] = useState<1 | 2>(1);
    const [selecting, setSelecting] = useState<OnboardingOption | null>(null);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<{ msg: string; staleSession: boolean } | null>(null);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await fetch('/api/onboarding/status', { credentials: 'include' });
                const json = await res.json();
                if (json?.ok && json.needsOnboarding) {
                    setNeedsOnboarding(true);
                }
            } catch (e) {
                console.error('Erro ao verificar onboarding', e);
            } finally {
                setLoading(false);
            }
        };
        checkStatus();
    }, []);

    const selectOption = async (opt: OnboardingOption) => {
        setSelecting(opt);
        setError(null);
        try {
            const res = await fetch('/api/onboarding/select', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ option: opt }),
            });
            const json = await res.json();
            if (json?.ok) {
                setResult(json);
                setStep(2);
            } else {
                setError({
                    msg: json?.msg || 'Não foi possível configurar o atendimento. Tente novamente.',
                    staleSession: json?.code === 'stale_session',
                });
            }
        } catch (e) {
            console.error('Erro ao selecionar onboarding', e);
            setError({ msg: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.', staleSession: false });
        } finally {
            setSelecting(null);
        }
    };

    const handleSairEEntrar = () => {
        window.location.href = '/logout';
    };

    const handleClose = () => {
        setNeedsOnboarding(false);
    };

    const handleTestarAtendimento = async () => {
        if (!result?.dialogo?.id) {
            handleClose();
            return;
        }

        try {
            // Obter JWT para o diálogo
            const res = await fetch('/api/auth/jwt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ did: result.dialogo.id }),
            });
            const json = await res.json();

            if (json?.result) {
                // Abrir popup do webchat
                window.open(
                    `/webchat/?a=${json.result}&t=${encodeURIComponent('/start')}`,
                    'webchatWindow',
                    'popup=1',
                );
            }
        } catch (e) {
            console.error('Erro ao abrir webchat', e);
        }

        handleClose();
    };

    if (loading || !needsOnboarding) return null;

    return (
        <Modal
            show={needsOnboarding}
            onClose={handleClose}
            closeable={step === 2 || !!error}
            size="lg"
            hideControls
            ignoreOverlayClick
            title={
                step === 1 ? (
                    <Subtitulo>
                        <HBox style={{ justifyContent: 'center', gap: 8 }}>
                            <Icon path={mdiRocketLaunch} size={2} color="var(--tc-color-primary)" />
                            <span>Configure seu primeiro atendimento</span>
                        </HBox>
                    </Subtitulo>
                ) : (
                    <Subtitulo>
                        <HBox style={{ justifyContent: 'center', gap: 8 }}>
                            <Icon path={mdiCheck} size={2} color="var(--tc-color-success)" />
                            <span>Tudo certo!</span>
                        </HBox>
                    </Subtitulo>
                )
            }
        >
            {step === 1 && (
                <VBox gap={16}>
                    <p style={{ textAlign: 'center', margin: 0, fontSize: '1.1em' }}>
                        Como será o primeiro atendimento da sua empresa?
                    </p>
                    {error && (
                        <AlertBox variant="danger">
                            <VBox gap={8}>
                                <span>{error.msg}</span>
                                {error.staleSession && (
                                    <HBox style={{ justifyContent: 'center' }}>
                                        <Botao variant="outline-danger" onClick={handleSairEEntrar}>
                                            Sair e entrar novamente
                                        </Botao>
                                    </HBox>
                                )}
                            </VBox>
                        </AlertBox>
                    )}
                    <div className={styles.optionsGrid}>
                        {options.map((opt) => (
                            <button
                                key={opt.key}
                                className={styles.optionCard}
                                disabled={!!selecting}
                                onClick={() => selectOption(opt.key)}
                            >
                                <Badge variant='outline-secondary'>
                                    <Icon path={mdiMedal} size={1.2} color="gold" />
                                    {opt.topChoice}
                                </Badge>
                                <Icon path={opt.icon} size={3} color="var(--tc-color-primary)" />
                                <strong>{opt.title}</strong>
                                <span>{opt.description}</span>
                                <span style={{ fontStyle: 'italic' }}>{opt.reason}</span>
                                <ul className={styles.bulletsList}>
                                    {opt.bullets.map((bullet, idx) => (
                                        <li key={idx}>{bullet}</li>
                                    ))}
                                </ul>
                                {selecting === opt.key && <span className={styles.loading}>Configurando...</span>}
                            </button>
                        ))}
                    </div>
                    <p style={{ textAlign: 'center', margin: 0, fontSize: '1rem', color: 'var(--tc-color-gray-900)' }}>
                        Em menos de 2 minutos você já poderá testar seu atendimento funcionando.
                    </p>
                    <p style={{ textAlign: 'center', margin: 0, fontSize: '0.8rem', color: 'var(--tc-color-gray-700)' }}>
                        Você pode alterar tudo depois, sem risco.
                    </p>
                </VBox>
            )}

            {step === 2 && (
                <Grid>
                    <GridItem sm={12} md={3}>&nbsp;</GridItem>
                    <GridItem sm={12} md={6}>
                        <VBox gap={16}>
                            <p style={{ textAlign: 'center', margin: 0, fontSize: '1.2em', fontWeight: 600 }}>
                                Seu atendimento está pronto!
                            </p>
                            <ul className={styles.checkList}>
                                <li>
                                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                                    Fluxo criado
                                </li>
                                <li>
                                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                                    Canal de testes ativo
                                </li>
                                <li>
                                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                                    Atendimento pronto para uso!
                                </li>
                            </ul>
                            <HBox style={{ justifyContent: 'center', gap: 12, marginTop: 16 }}>
                                <Botao variant="primary" onClick={handleTestarAtendimento}>
                                    <Icon path={mdiRocketLaunch} size={1.2} />
                                    Testar atendimento agora
                                </Botao>
                            </HBox>
                        </VBox>
                    </GridItem>
                    <GridItem sm={12} md={3}>&nbsp;</GridItem>
                </Grid>
            )}
        </Modal>
    );
};

export default OnboardingModal;
